import { RestApiResponse } from "./RestApi";
import {
  AccountAndBranchesAttributes,
  ActivityRequestAttributes,
  AddBranchManagerResponseAttributes,
  ApproveEmployeeAttributes,
  BranchManagerDashboardRequestAttributes,
  DeclineEmployeeAttributes,
  GetEmployeeDetailAttributes,
  InviteEmployeeAttributes,
  ListEmployeesAttributes,
  RequestMoreInfoFromEmployeeAttributes,
} from "../attributes/branch-manager-attributes";
import { BaseResponseAttributes } from "../attributes/base-attributes";
import { AnnouncementsAttributes } from "../attributes/announcements-attributes";
import BaseService from "./BaseService";
import { base64toFile } from "../common/Hooks";

export class BranchManagerService extends BaseService {
  static async resume(
    data: BranchManagerDashboardRequestAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().post(
      `business-branches/resume`,
      data
    );
  }

  static async inviteEmployee(
    data: InviteEmployeeAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().post(
      `business-branches/invite-employee`,
      data
    );
  }

  static async listEmployees(
    data: ListEmployeesAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().post(
      `business-branches/employees`,
      data
    );
  }

  static async getEmployeeDetail(
    data: GetEmployeeDetailAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().post(
      `business-branches/employee-detail`,
      data
    );
  }

  static async approveEmployee(
    data: ApproveEmployeeAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().post(
      `business-branches/approve-employee`,
      data
    );
  }

  static async declineEmployee(
    data: DeclineEmployeeAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().post(
      `business-branches/decline-employee`,
      data
    );
  }

  static async requestMoreInfoFromEmployee(
    data: RequestMoreInfoFromEmployeeAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().post(
      `business-branches/request-more-info-from-employee`,
      data
    );
  }

  static async getActivities(
    data: ActivityRequestAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().post(
      `business-branches/activities`,
      data
    );
  }

  static async getAccountAndBranches(
    data: AccountAndBranchesAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().post(
      `business-branches/account-and-branches`,
      data
    );
  }

  static async add(
    data: AddBranchManagerResponseAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    const url: string = "business-branches/add-manager";

    return BranchManagerService.getApiClient().post(url, data);
  }

  static async getOpeningHours(
    branchId: number
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().get(
      `business-branches/${branchId}/opening-hours`
    );
  }

  static async getDaysForId(
    id: number
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().get(
      `business-branches/${id}/days-opening-hours`
    );
  }

  static async addAnnouncement(
    branchId: number,
    data: AnnouncementsAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    const url: string = "announcements/create";

    const formData = new FormData();
    formData.append("business_branch_id", `${branchId}`);

    // Add text data
    Object.keys(data).forEach((key) => {
      if ("images" !== key.toString()) {
        formData.append(key, data[key]);
      }
    });
    // Add file data registration photo
    if (data.images !== null && data.images !== undefined) {
      formData.append("images", base64toFile(data.images, "image.png"));
    }

    return BranchManagerService.getApiClient().sendFormData({
      method: "post",
      url: url,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  static async updateAnnouncement(
    announcementId: number,
    data: AnnouncementsAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    const url: string = `announcements/${announcementId}/update`;

    const formData = new FormData();

    // Add text data
    Object.keys(data).forEach((key) => {
      if ("images" !== key.toString()) {
        formData.append(key, data[key]);
      }
    });
    // Add file data registration photo
    if (data.images !== null && data.images !== undefined) {
      formData.append("images", base64toFile(data.images, "image.png"));
    }

    return BranchManagerService.getApiClient().sendFormData({
      method: "post",
      url: url,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  static async branchAnnouncements(
    branchId: number,
    page?: number
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().get(
      `business-branches/${branchId}/announcements?page=${page}`,
      {
        params: { page: page },
      }
    );
  }

  static async getAnnouncement(
    id: number
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().get(
      `business-branches/announcement/${id}`
    );
  }

  static async addAnnouncement2(
    branchId: number,
    data: AnnouncementsAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    const url: string = "announcements/create";

    const formData = new FormData();
    formData.append("business_branch_id", `${branchId}`);

    return BranchManagerService.getApiClient().sendFormData({
      method: "post",
      url: url,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  static async resumeRefund(
    data: BranchManagerDashboardRequestAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().post(
      `business-branches/resume-refund`,
      data
    );
  }

  static async resumeWithdraw(
    data: BranchManagerDashboardRequestAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().post(
      `business-branches/resume-withdraw`,
      data
    );
  }

  static async resumeRecharge(
    data: BranchManagerDashboardRequestAttributes
  ): Promise<RestApiResponse<BaseResponseAttributes>> {
    return BranchManagerService.getApiClient().post(
      `business-branches/resume-recharge`,
      data
    );
  }
}

export default BranchManagerService;
